<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    ref="container"
    class="selectable-tiles-container"
    :class="{ 'auto-width': useAutoWidth, 'read-only': isReadOnly }">
    <!-- SEARCH BOX -->
    <div
      v-if="searchEnabled"
      class="search-box">
      <input
        v-model="itemSearchTerm"
        class="form-control search-field"
        :placeholder="searchHint" />
      <a
        v-show="itemSearchTerm !== ''"
        class="clear-search-term"
        @click="itemSearchTerm = ''"
        >{{ $t('actions.clear') }}</a
      >
    </div>

    <!-- TILES -->
    <div
      class="selectable-tiles"
      :class="{
        readonly: isReadOnly,
        'has-no-selection': selectedItems === null || selectedItems.length === 0,
        'justify-left': justifyContentLeft,
      }">
      <slot
        v-if="isLoading"
        name="loading" />
      <slot
        v-else-if="itemsToShow.length === 0"
        name="no-items" />
      <div
        v-for="(item, i) in itemsToShow"
        v-else
        :id="`${item?.id || i}`"
        :key="`${item[labelPropertyName]} - ${item[descriptionPropertyName]}`"
        v-tooltip="
          item[isDisabledPropertyName]
            ? item[disabledMessagePropertyName] || item[tooltipPropertyName]
            : item[tooltipPropertyName]
        "
        :data-test-id="item.id"
        class="selectable-tile"
        :data-entity-type="item[labelPropertyName]"
        :class="{
          isSelected: isItemSelected(item),
          horizontal: !isTileContentLayoutVertical,
          isDisabled: (disabledUnselectedTiles && !isItemSelected(item)) || item[isDisabledPropertyName],
          compact: i + 1 > maximumNumberOfLargeTiles,
        }"
        :style="selectableTileStyle"
        @click="
          () => {
            disabledUnselectedTiles ? {} : toggleItem(item);
          }
        "
        @mouseenter="$emit('hoveringOn', item)"
        @mouseleave="$emit('hoveringOn', null)">
        <!-- ICONS (svg packed with app)-->
        <div
          v-if="shouldDisplayIcons"
          class="icon"
          :style="`width: ${iconSize}; height: ${iconSize}; margin-bottom: ${iconMarginBottom}; ${iconMarginRight != null ? ` margin-right: ${iconMarginRight};` : ''}`">
          <component :is="item[iconPropertyName]" />
        </div>
        <!-- IMAGES (external, e.g.: company logos) -->
        <img
          v-if="shouldDisplayImages && item[imageSourcePropertyName] !== null && item[imageSourcePropertyName] !== ''"
          :src="item[imageSourcePropertyName]"
          :alt="item[labelPropertyName]" />
        <div
          v-if="
            (shouldDisplayImages || shouldDisplayIcons) && shouldDisplayLabels && item[imageSourcePropertyName] != null
          "
          class="separator"
          :style="`height: ${separatorHeight};`" />

        <!-- MAIN TEXT -->
        <span
          v-if="
            shouldDisplayLabels || (useLabelWhenNoImage && shouldDisplayImages && item[imageSourcePropertyName] == null)
          "
          :class="{
            'badge badge-xs badge-muted':
              shouldDisplayImages && item[imageSourcePropertyName] !== null && item[imageSourcePropertyName] !== '',
          }">
          {{ item[labelPropertyName] }}</span
        >

        <!-- DESCRIPTION -->
        <span
          v-if="shouldDisplayLabels && item[descriptionPropertyName] != null"
          :class="`mt-2 badge badge-rounded badge-sm ${showPrimaryBadgeColor ? 'badge-primary' : 'badge-muted'}`"
          >{{ item[descriptionPropertyName] }}</span
        >

        <!-- CHECKMARK -->
        <div
          v-if="shouldDisplayCheckmarkOnSelectedItems"
          class="checkmark">
          <icon-check-fat />
        </div>

        <div
          v-if="item.note"
          class="special-note">
          {{ item.note }}
        </div>

        <!-- ONLY BUTTON -->
        <button
          v-if="!isReadOnly && isMultiselect && showOnlyButton && !item[isDisabledPropertyName]"
          class="only-btn btn btn-primary-hollow btn-xs btn-rounded"
          @click.stop="selectOnly(item)">
          {{ $t('messages.only') }}
        </button>

        <!-- TOP RIGHT BADGE (useful to show quantities) -->
        <div
          v-if="item[topRightBadgeContentPropertyName] != null"
          v-sanitize-html="item[topRightBadgeContentPropertyName]"
          class="top-right-badge" />
        <slot
          name="top-right-badge"
          class="top-right-badge"
          v-bind="item" />
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import IconCheckFat from '@/common/icons/monochrome/CheckFat';
  import { convertToPixels } from '@/globals';

  export default {
    components: {
      IconCheckFat,
    },
    props: {
      items: {
        type: Array,
      },
      shouldDisplayLabels: {
        type: Boolean,
        default: true,
      },
      shouldDisplayImages: {
        type: Boolean,
        default: false,
      },
      shouldDisplayIcons: {
        type: Boolean,
        default: false,
      },
      labelPropertyName: {
        type: String,
        default: null,
      },
      iconPropertyName: {
        type: String,
        default: null,
      },
      iconSize: {
        type: String,
        default: '4.8rem',
      },
      iconMarginBottom: {
        type: String,
        default: '0',
      },
      iconMarginRight: {
        type: String,
        default: null,
      },
      descriptionPropertyName: {
        type: String,
        default: null,
      },
      tooltipPropertyName: {
        type: String,
        default: null,
      },
      imageSourcePropertyName: {
        type: String,
        default: null,
      },
      selectedItems: {
        type: Array,
        default: null,
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
      isMultiselect: {
        type: Boolean,
        default: true,
      },
      allowEmpty: {
        type: Boolean,
        default: true,
      },
      searchEnabled: {
        type: Boolean,
        default: false,
      },
      searchHint: {
        type: String,
        default: '',
      },
      shouldDisplayCheckmarkOnSelectedItems: {
        type: Boolean,
        default: false,
      },
      isTileContentLayoutVertical: {
        type: Boolean,
        default: true,
      },
      tileWidth: {
        type: String,
        default: null,
      },
      minTileWidth: {
        type: String,
        default: null,
      },
      justifyContentLeft: {
        type: Boolean,
        default: false,
      },
      tilePadding: {
        type: String,
      },
      useAutoWidth: {
        type: Boolean,
        default: false,
      },
      tileMarginBottom: {
        type: String,
        default: null,
      },
      tileMarginRight: {
        type: String,
        default: '1.2rem',
      },
      separatorHeight: {
        type: String,
        default: '0.5rem',
      },
      showPrimaryBadgeColor: {
        type: Boolean,
        default: false,
      },
      topRightBadgeContentPropertyName: {
        type: String,
        default: null,
      },
      isDisabledPropertyName: {
        type: String,
        default: null,
      },
      disabledMessagePropertyName: {
        type: String,
        default: null,
      },
      maximumNumberOfLargeTiles: {
        type: Number,
        default: Number.MAX_VALUE,
      },
      showOnlyButton: {
        type: Boolean,
        default: true,
      },
      disabledUnselectedTiles: {
        type: Boolean,
        default: false,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      useLabelWhenNoImage: {
        type: Boolean,
        default: false,
      },
      itemsToNotToggle: {
        type: Array,
        default: () => [],
      },
      itemsSearchTermInternal: {
        type: String,
        default: '',
      },
      trackByProperty: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        selectedItemsInternal: [],
        itemSearchTerm: '',
        internalTileWidth: null,
        internalMaxTileWidth: null,
      };
    },
    computed: {
      hasSearchTerm() {
        return this.itemSearchTerm !== null && this.itemSearchTerm.trim() !== '';
      },
      itemsToShow() {
        if (this.hasSearchTerm) {
          return this.items.filter((item) =>
            item[this.labelPropertyName].toLowerCase().includes(this.itemSearchTerm.toLowerCase()),
          );
        }
        return this.items;
      },
      selectableTileStyle() {
        let style = '';
        if (this.tileMarginBottom != null) {
          style = `${style}margin-bottom: ${this.tileMarginBottom};`;
        }
        if (this.tileMarginRight != null) {
          style = `${style}margin-right: ${this.tileMarginRight};`;
        }
        if (this.tilePadding != null) {
          style = `${style}padding: ${this.tilePadding};`;
        }
        if (this.tileWidth != null) {
          style = `${style}flex-basis: ${this.tileWidth};`;
        } else {
          style = `${style}flex-basis: ${this.internalTileWidth};`;
          style = `${style}max-width: ${this.internalMaxTileWidth};`;
        }
        if (this.minTileWidth != null) {
          style = `${style}min-width: ${this.minTileWidth};`;
        }
        return style;
      },
    },
    watch: {
      selectedItems: {
        deep: true,
        handler(newVal) {
          if (newVal != null) {
            this.selectedItemsInternal = [...newVal];
          } else {
            this.selectedItemsInternal = [];
          }
        },
      },
    },
    mounted() {
      this.itemSearchTerm = this.itemsSearchTermInternal;
      if (this.selectedItems !== null) {
        this.selectedItemsInternal = [...this.selectedItems];
      }
      window.addEventListener('resize', this.onContainerResized);
      this.onContainerResized();
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.onContainerResized);
    },
    methods: {
      isItemSelected(item) {
        if (item.id != null) {
          return this.selectedItemsInternal
            .filter((x) => x != null)
            .map((x) => x.id)
            .includes(item.id);
        }
        if (this.trackByProperty) {
          return (
            this.selectedItemsInternal.findIndex(
              (item1) => item1[this.trackByProperty] === item[this.trackByProperty],
            ) > -1
          );
        }
        return this.selectedItemsInternal.includes(item);
      },
      toggleItem(item) {
        if (this.isReadOnly || (this.isDisabledPropertyName != null && item[this.isDisabledPropertyName] === true)) {
          return;
        }

        if (this.isMultiselect && this.isItemCanBeToggled(item?.id)) {
          if (this.isItemSelected(item)) {
            if (this.selectedItems.length > 1 || this.allowEmpty) {
              if (this.trackByProperty) {
                this.selectedItemsInternal.splice(
                  this.selectedItemsInternal.findIndex(
                    (item1) => item1[this.trackByProperty] === item[this.trackByProperty],
                  ),
                  1,
                );
              } else if (item != null && item.id != null) {
                this.selectedItemsInternal = this.selectedItemsInternal.filter((x) => x?.id !== item.id);
              } else {
                this.selectedItemsInternal.splice(this.selectedItemsInternal.indexOf(item), 1);
              }
            }
          } else {
            this.selectedItemsInternal.push(item);
          }
        } else if (this.selectedItemsInternal.find((x) => x === item) != null && this.allowEmpty) {
          this.selectedItemsInternal = [];
        } else {
          this.selectedItemsInternal = [item];
        }
        this.$emit('selectionChanged', this.selectedItemsInternal);
      },
      selectOnly(item) {
        this.selectedItemsInternal = [item];
        this.$emit('selectionChanged', this.selectedItemsInternal);
      },
      onContainerResized() {
        if (this.tileWidth != null) {
          return;
        }
        if (this.resizeTimer != null) {
          clearTimeout(this.resizeTimer);
        }

        this.resizeTimer = setTimeout(() => {
          this.resizeTimer = null;
          let minTileWidth = convertToPixels('20rem');
          if (this.minTileWidth != null) {
            minTileWidth = convertToPixels(this.minTileWidth);
          }
          if (this.tileMarginRight != null) {
            minTileWidth += convertToPixels(this.tileMarginRight);
          }
          if (this.$refs.container != null) {
            const containerWidth = this.$refs.container.clientWidth;
            const maxHorizontalTiles = Math.max(1, Math.floor(containerWidth / minTileWidth));
            this.internalTileWidth = `${100 * (1 / maxHorizontalTiles)}%`;
            this.internalMaxTileWidth = `${Math.floor(containerWidth / maxHorizontalTiles) - convertToPixels(this.tileMarginRight) - 1}px`;
          }
        }, 100);
      },
      isItemCanBeToggled(id) {
        if (!id) {
          return true;
        }
        return !this.itemsToNotToggle.some((x) => x === id);
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .selectable-tiles-container {
    width: 100%;

    &.auto-width {
      width: auto;
    }

    &.read-only {
    }
  }

  .selectable-tiles {
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;

    &.justify-left {
      justify-content: flex-start;
    }

    .selectable-tile {
      min-width: 20rem;
      padding: 3.2rem;
      margin-bottom: 1.6rem;
      border: solid 0.1rem $borders;
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      position: relative;
      box-shadow: $tiny-shadow;
      transition:
        border 0.2s ease-in-out,
        filter 0.2s ease-in-out,
        opacity 0.2s ease-in-out;

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.horizontal {
        flex-direction: row;
        justify-content: center;

        span {
          flex-basis: 100%;
          text-align: left;
          margin: initial;
        }

        .icon {
          margin-right: 1.6rem;
          margin-bottom: 0;
        }

        button {
          margin: auto;
        }
      }

      img {
        transition:
          filter 0.2s ease-in-out,
          opacity 0.2s ease-in-out;
        max-width: 100%;
        max-height: 3.2rem;
        margin: auto;
        filter: grayscale(100%);
        opacity: 0.5;
      }

      span {
        transition:
          filter 0.2s ease-in-out,
          opacity 0.2s ease-in-out;
        margin: auto;
        font-weight: 700;
        text-align: center;
      }

      svg {
        transition:
          filter 0.2s ease-in-out,
          opacity 0.2s ease-in-out;
        opacity: 0.5;
        width: 80%;
        filter: grayscale(100%);
      }

      .separator {
        height: 1rem;
      }

      &.isSelected {
        transition:
          filter 0.2s ease-in-out,
          opacity 0.2s ease-in-out;

        border-color: var(--secondaryColorLighter);
        box-shadow: var(--panesShadow);

        img {
          filter: none;
          opacity: 1;
        }

        span {
          transition:
            filter 0.2s ease-in-out,
            opacity 0.2s ease-in-out;
          opacity: 1;
        }

        svg {
          transition:
            filter 0.2s ease-in-out,
            opacity 0.2s ease-in-out;
          filter: none;
          opacity: 1;
        }

        .checkmark {
          display: flex;

          svg {
            stroke-width: 0.2rem;
          }
        }
      }

      &.isDisabled {
        cursor: not-allowed;
        img,
        svg {
          filter: grayscale(100%) !important;
          opacity: 0.5;
        }

        > span {
          opacity: 0.5;
        }

        .badge {
          opacity: 1;
        }
      }

      .badge {
        position: absolute;
        bottom: -1rem;
        opacity: 1;
        border: solid 0.1rem #ddd;
      }

      &.compact {
        padding: 1rem;

        img,
        svg {
          max-width: 100%;
          max-height: 1.6rem;
        }
      }

      .checkmark {
        background-color: var(--secondaryColor);

        display: none;
        position: absolute;
        top: -0.8rem;
        right: -0.8rem;
        width: 2.8rem;
        height: 2.8rem;
        border-radius: 1.4rem;
        flex-direction: column;

        svg {
          fill: var(--secondaryColorReverse);

          width: 1rem;
          height: 1rem;
          margin: auto;
        }
      }

      .top-right-badge {
        position: absolute;
        top: -0.8rem;
        right: -0.8rem;
      }

      .only-btn {
        position: absolute;
        bottom: -1.2rem;
        transform: scale(0);
        opacity: 0 !important;
        display: none !important;
      }

      .special-note {
        margin-left: auto;
        margin-bottom: -1rem;
        background: var(--secondaryColor);
        color: var(--secondaryColorReverse) !important;
        border-radius: 0.8rem;
        font-size: 1.2rem;
        padding: 0.35rem;
        font-family: $monospace-font-family;
      }

      &:hover {
        .only-btn {
          transition: all 0.2s $bezier-ease-out;
          transform: scale(1);
          opacity: 1 !important;
          display: block !important;
        }
      }

      //.small-text {
      //  color: var(--textMutedColor);
      //  font-size: 1rem;
      //}
    }

    &.readonly {
      margin-bottom: -1.6rem;

      > div {
        cursor: auto;
      }
    }

    &.has-no-selection {
      > div {
        img {
          filter: none;
          opacity: 1;
        }

        span {
        }
      }
    }
  }

  .search-box {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    margin-bottom: 1rem;

    .search-field {
      flex-grow: 1;
      display: inline-flex;
      width: auto;
    }

    .clear-search-term {
      display: inline-flex;
      margin-left: 1rem;
      cursor: pointer;
    }
  }

  #deal_exchange_buyer_restrictions {
    img,
    svg {
      filter: none;
      opacity: 1;
    }
    .selectable-tiles {
      border-color: var(--secondaryColorLighter);
      box-shadow: var(--panesShadow);
      img,
      svg {
        filter: none;
        opacity: 1;
      }

      .selectable-tile {
        img {
          filter: none;
          opacity: 1;
        }
        &.isDisabled {
          cursor: not-allowed;
          img,
          svg {
            filter: grayscale(100%) !important;
            opacity: 0.5;
          }

          > span {
            opacity: 0.5;
          }

          .badge {
            opacity: 1;
          }
        }
      }
    }
  }
</style>
